/**
 * @fileoverview Template assistant chat panel (MOR-962)
 *
 * Chat sidebar for the report-template editor, mirroring the job report's
 * assistant: @-mention a section to target it, or highlight a passage in a
 * wording box to scope the edit to just that text. Edits apply directly to
 * the template sections (no propose/apply staging).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import {
  ChatInput,
  ChatMessage,
  type ChatMentionItem,
  type ChatMessageData,
} from "@/ui/composed/chat";
import { Loader2, MessageSquare, Wrench, X } from "lucide-react";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errors";

/** A passage the user highlighted in a section's wording box. */
export interface TemplateChatSelection {
  sectionTitle: string;
  excerpt: string;
}

/** A tool-call pill for a section-update step in the template chat. */
function ToolPill({ label, active }: { label: string; active: boolean }) {
  return (
    <div className="px-5 py-1.5">
      <div className="inline-flex items-center gap-2 rounded-lg border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
        {active ? (
          <Loader2 className="size-3.5 animate-spin text-primary" />
        ) : (
          <Wrench className="size-3.5 text-primary" />
        )}
        <span className="font-medium text-foreground">{label}</span>
      </div>
    </div>
  );
}

export function TemplateChatPanel({
  templateId,
  sectionTitles,
  selections,
  onRemoveSelection,
  onClearSelections,
  onClose,
}: {
  templateId: Id<"reportTemplates">;
  sectionTitles: string[];
  /** Passages highlighted in wording boxes (chips in the composer). */
  selections: TemplateChatSelection[];
  onRemoveSelection: (index: number) => void;
  onClearSelections: () => void;
  onClose: () => void;
}) {
  const { data: messages } = useQuery(
    convexQuery(api.settings.reports.queries.listTemplateChatMessages, {
      templateId,
    }),
  );
  const { mutateAsync: sendMessage, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.reports.mutations.sendTemplateChatMessage,
    ),
  });

  const stored = messages ?? [];
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [stored.length]);

  const mentionItems = useMemo<ChatMentionItem[]>(
    () =>
      sectionTitles.map((title) => ({
        id: `section:${title}`,
        name: title,
        subtitle: "Section",
      })),
    [sectionTitles],
  );

  // While the tail of the thread is a user message or a tool pill, the
  // responder is still working.
  const last = stored[stored.length - 1];
  const isThinking =
    isPending || last?.role === "user" || last?.kind === "tool";

  const [composerKey, setComposerKey] = useState(0);

  const handleSend = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Tags are re-derived from the text so a deleted @-tag doesn't linger.
    const taggedSections = sectionTitles.filter((t) =>
      trimmed.includes(`@${t}`),
    );
    try {
      await sendMessage({
        templateId,
        message: trimmed,
        taggedSections: taggedSections.length > 0 ? taggedSections : undefined,
        selections: selections.length > 0 ? selections : undefined,
      });
      onClearSelections();
      setComposerKey((k) => k + 1);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to send"));
    }
  };

  return (
    <div className="sticky top-20 flex h-[calc(100vh-9rem)] w-[380px] shrink-0 flex-col overflow-hidden rounded-xl border bg-background">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <MessageSquare className="size-4 text-muted-foreground" />
          Assistant
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close assistant"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto py-2">
        {stored.length === 0 && (
          <div className="px-5 py-6 text-sm text-muted-foreground">
            Ask for changes to this template — type @ to tag a section, or
            highlight a passage in a wording box to target just that text.
          </div>
        )}
        {stored.map((m, i) =>
          m.kind === "tool" ? (
            <ToolPill
              key={m._id}
              label={m.content}
              active={i === stored.length - 1 && isThinking}
            />
          ) : (
            <ChatMessage
              key={m._id}
              message={
                {
                  id: m._id,
                  role: m.role,
                  content: m.content,
                  createdAt: m.createdAt,
                } satisfies ChatMessageData
              }
            />
          ),
        )}
        {isThinking && last?.kind !== "tool" && (
          <div className="flex items-center gap-2 px-5 py-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Thinking…
          </div>
        )}
      </div>

      {selections.length > 0 && (
        <div className="space-y-1.5 border-t px-3 pt-2">
          {selections.map((sel, i) => (
            <div
              key={`${sel.sectionTitle}-${i}`}
              className="flex items-start gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs"
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium">
                  Selection · {sel.sectionTitle}
                </div>
                <div className="truncate text-muted-foreground">
                  “{sel.excerpt}”
                </div>
              </div>
              <button
                type="button"
                onClick={() => onRemoveSelection(i)}
                aria-label="Remove selection"
                className="mt-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ))}
          {selections.length > 1 && (
            <button
              type="button"
              onClick={onClearSelections}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      <div className="p-3">
        <ChatInput
          key={composerKey}
          onSend={handleSend}
          isDisabled={isPending}
          placeholder={
            selections.length > 0
              ? "What should change in the selected passages?"
              : "Improve this template… type @ to tag a section"
          }
          mentionItems={mentionItems}
          mentionLabel="Tag a section"
        />
      </div>
    </div>
  );
}
